import { isAfter, isToday, isValid, parseISO } from 'date-fns';
import type { Booking } from '@/features/bookings-management/types';
import { tableSortFilter } from '@/utils/arrayProcessing';

export type ClientBookingSessionFilter =
  | 'active'
  | 'today'
  | 'upcoming'
  | 'noFuture'
  | 'all';

export type ClientBookingSessionDetailFilter =
  | 'all'
  | 'today'
  | 'upcoming'
  | 'previous';

export const CLIENT_BOOKING_SESSION_FILTER_TABS: {
  value: ClientBookingSessionFilter;
  label: string;
}[] = [
  { value: 'active', label: 'Active sessions' },
  { value: 'today', label: 'Today' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'noFuture', label: 'No future sessions' },
  { value: 'all', label: 'All' },
];

export const CLIENT_BOOKINGS_TABLE_HEAD = [
  { id: 'emptyCell' },
  { id: 'id', label: 'Booking ID', align: 'left' },
  { id: 'date', label: 'Booking start', align: 'left' },
  { id: 'total_amount', label: 'Total amount', align: 'left' },
  {
    id: 'booking_service.service_name',
    label: 'Booking service',
    align: 'left',
  },
  { id: 'service_variant.name', label: 'Service variant', align: 'left' },
  { id: 'sessions', label: 'Sessions', align: 'left', minWidth: 190 },
  { id: 'address.formatted_address', label: 'Address', align: 'left' },
  { id: 'booking_status.display', label: 'Booking status', align: 'left' },
  { id: 'cleaner', label: 'Cleaner', align: 'left' },
  { id: 'actionButtons', label: '', align: 'left' },
];

export const CLIENT_BOOKINGS_TABLE_COLUMN_COUNT =
  CLIENT_BOOKINGS_TABLE_HEAD.length;

const getPositiveCount = (value: unknown) => {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

type ClientBookingSessionCounts = {
  previous: number;
  today: number;
  upcoming: number;
  active: number;
  total: number;
};

const parseSessionDate = (value: unknown) => {
  if (typeof value !== 'string') return null;

  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const isoDate = parseISO(trimmedValue);
  if (isValid(isoDate)) return isoDate;

  const parsedDate = new Date(trimmedValue);
  return isValid(parsedDate) ? parsedDate : null;
};

const getFutureSessionDateCounts = (
  nextCleaningSessions: unknown
): Pick<ClientBookingSessionCounts, 'today' | 'upcoming'> => {
  if (!Array.isArray(nextCleaningSessions)) {
    return { today: 0, upcoming: 0 };
  }

  return nextCleaningSessions.reduce(
    (counts, dateValue) => {
      const sessionDate = parseSessionDate(dateValue);
      if (!sessionDate) return counts;

      if (isToday(sessionDate)) {
        counts.today += 1;
      } else if (isAfter(sessionDate, new Date())) {
        counts.upcoming += 1;
      }

      return counts;
    },
    { today: 0, upcoming: 0 }
  );
};

type ClientBookingSessionCountSource = Pick<Booking, 'booking_sessions_count'> &
  Partial<Pick<Booking, 'next_cleaning_sessions'>>;

export const getClientBookingSessionCounts = (
  booking: ClientBookingSessionCountSource
): ClientBookingSessionCounts => {
  const counts = booking.booking_sessions_count;
  const futureSessionDateCounts = getFutureSessionDateCounts(
    booking.next_cleaning_sessions
  );
  const previous = getPositiveCount(counts?.completed);
  const today = Math.max(
    getPositiveCount(counts?.today),
    futureSessionDateCounts.today
  );
  const upcoming = Math.max(
    getPositiveCount(counts?.upcoming),
    futureSessionDateCounts.upcoming
  );
  const active = today + upcoming;

  return {
    previous,
    today,
    upcoming,
    active,
    total: previous + active,
  };
};

export const getPreferredClientBookingSessionFilter = (
  booking: ClientBookingSessionCountSource
): ClientBookingSessionDetailFilter => {
  const counts = getClientBookingSessionCounts(booking);

  if (counts.today > 0) return 'today';
  if (counts.upcoming > 0) return 'upcoming';
  return 'previous';
};

export const bookingMatchesClientBookingSessionFilter = (
  booking: ClientBookingSessionCountSource,
  filterStatus: ClientBookingSessionFilter | string
) => {
  const counts = getClientBookingSessionCounts(booking);
  const normalizedFilter = filterStatus.trim().toLowerCase();

  switch (normalizedFilter) {
    case 'active':
    case 'active sessions':
      return counts.active > 0;
    case 'today':
      return counts.today > 0;
    case 'upcoming':
      return counts.upcoming > 0;
    case 'nofuture':
    case 'no future sessions':
      return counts.active === 0;
    case 'all':
      return true;
    default:
      return true;
  }
};

export function applySortFilterClientBookings({
  tableData,
  comparator,
  searchFieldValue,
  filterStatus,
}: {
  tableData: Booking[];
  comparator: (a: any, b: any) => number;
  searchFieldValue: string;
  filterStatus: string;
}) {
  tableData = tableSortFilter(tableData, comparator);
  const normalizedSearchFieldValue = searchFieldValue.trim();
  const searchFieldLowerCase = normalizedSearchFieldValue.toLowerCase();

  if (normalizedSearchFieldValue) {
    tableData = tableData.filter((booking) => {
      const formattedAddress = booking.address?.formatted_address ?? '';
      const postcode = booking.address?.postcode ?? '';
      const matchesClientDetails =
        booking.id.toString().indexOf(normalizedSearchFieldValue) !== -1 ||
        formattedAddress.toLowerCase().indexOf(searchFieldLowerCase) !== -1 ||
        postcode.toLowerCase().indexOf(searchFieldLowerCase) !== -1 ||
        postcode
          .replace(/\s/g, '')
          .toLowerCase()
          .indexOf(searchFieldLowerCase) !== -1;

      const matchesCleanerName = booking.booking_cleaners?.some((cleaner) => {
        const cleanerFullName = `${cleaner.first_name} ${cleaner.last_name}`;
        return (
          cleanerFullName.toLowerCase().indexOf(searchFieldLowerCase) !== -1
        );
      });

      const matchesAmount =
        (booking.total_amount ?? 0)
          .toString()
          .indexOf(normalizedSearchFieldValue) !== -1;

      return matchesClientDetails || matchesCleanerName || matchesAmount;
    });
  }

  tableData = tableData.filter((booking) =>
    bookingMatchesClientBookingSessionFilter(booking, filterStatus)
  );

  return tableData;
}
